import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, UsersRound } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * Subnav Communities dropdown — org-owned spaces for workplace Circa Social.
 */
export default function OrgSocialCommunitiesMenu({
  orgId,
  communities = [],
  communityPath,
}) {
  const location = useLocation();
  const manageHref = `/orgs/${encodeURIComponent(String(orgId))}/settings?section=community`;

  const sorted = useMemo(() => {
    const rows = Array.isArray(communities) ? [...communities] : [];
    rows.sort((a, b) => {
      const ap = a.is_primary_for_org ? 0 : 1;
      const bp = b.is_primary_for_org ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return String(a.display_name || "").localeCompare(
        String(b.display_name || "")
      );
    });
    return rows;
  }, [communities]);

  const activeCommunityId = useMemo(() => {
    const m = String(location.pathname || "").match(/\/c\/([^/]+)\/?$/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.pathname]);

  const isActive =
    activeCommunityId != null &&
    sorted.some(
      (c) => String(c.community_id) === String(activeCommunityId)
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm whitespace-nowrap transition-colors outline-none",
          "focus-visible:ring-2 focus-visible:ring-ring",
          isActive
            ? "bg-primary/15 text-foreground font-medium"
            : "text-muted-foreground hover:bg-muted/60"
        )}
      >
        <UsersRound className="h-4 w-4 shrink-0" />
        Communities
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Owned community spaces</DropdownMenuLabel>
        {sorted.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">
            No communities yet. Enable or manage Community Spaces in settings.
          </div>
        ) : (
          sorted.map((c) => {
            const id = c.community_id;
            const href = communityPath(id);
            const itemActive = String(id) === String(activeCommunityId);
            return (
              <DropdownMenuItem key={id} asChild>
                <Link
                  to={href}
                  className={cn(
                    "flex flex-col items-start gap-0.5 cursor-pointer",
                    itemActive && "bg-accent"
                  )}
                >
                  <span className="max-w-full truncate font-medium" title={c.display_name}>
                    {c.display_name}
                  </span>
                  {c.is_primary_for_org ? (
                    <span className="text-[11px] text-muted-foreground">
                      Primary
                    </span>
                  ) : null}
                </Link>
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={manageHref} className="cursor-pointer">
            Manage communities
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
